import { useEffect, useState } from "react";
import { Modal, Button } from "antd";
import { CheckOutlined, DownloadOutlined, ExportOutlined, FileTextOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import DocumentRowIcon from "./DocumentRowIcon";
import CheckInAgreementModal from "../../../../checkin/components/CheckInAgreementModal";
import { useToast } from "../../../../../contexts/ToastContext";

const AgreementModal = ({ agreementUrl, resDetails, isModalOpen, onClose, t }) => (
    <Modal
        title={<span className="text-lg font-medium">{t("rentalAgreement")}</span>}
        open={isModalOpen}
        onCancel={onClose}
        footer={[
            <Button
                key="download"
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => {
                    const link = document.createElement("a");
                    link.href = agreementUrl;
                    link.download = `rental-agreement-${resDetails.id || "document"}.pdf`;
                    link.target = "_blank";
                    link.click();
                }}>
                {t("download")}
            </Button>,
            <Button key="close" onClick={onClose}>
                {t("close")}
            </Button>,
        ]}
        width={900}
        style={{ top: 20 }}
        destroyOnClose>
        <div className="w-full" style={{ height: "calc(100vh - 200px)", padding: 0 }}>
            <div className="min-h-[600px] h-full w-full bg-gray-50">
                <iframe
                    src={`${agreementUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                    width="100%"
                    height="100%"
                    className="h-full w-full border-0"
                    title="Rental Agreement PDF Viewer"
                    style={{ minHeight: "600px" }}
                    allow="fullscreen"
                />
            </div>
        </div>
    </Modal>
);

AgreementModal.propTypes = {
    agreementUrl: PropTypes.string.isRequired,
    resDetails: PropTypes.object,
    isModalOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};

const RentalAgreementRow = ({ resDetails, t, onRefresh, signedDate }) => {
    const toast = useToast();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
    const [agreementUrl, setAgreementUrl] = useState(resDetails?.agreementDocUrl || "");

    useEffect(() => {
        setAgreementUrl(resDetails?.agreementDocUrl || "");
    }, [resDetails?.agreementDocUrl]);

    const handleAgreementGenerated = async (agreementDocUrl) => {
        setAgreementUrl(agreementDocUrl);
        setIsGenerateModalOpen(false);
        toast.success({ message: t("generateAgreementSuccess") });
        onRefresh?.();
    };

    const subtext = agreementUrl
        ? signedDate
            ? t("upcomingDetails.signedOn", { date: signedDate })
            : t("upcomingDetails.agreementAvailable")
        : t("noRentalAgreement");

    return (
        <>
            <div className="flex items-center justify-between gap-4 rounded-xl border border-[#f0f0f0] bg-white px-4 py-4">
                <div className="flex min-w-0 items-center gap-3">
                    <DocumentRowIcon
                        tone={agreementUrl ? "success" : "neutral"}
                        icon={
                            agreementUrl ? (
                                <CheckOutlined className="text-lg" />
                            ) : (
                                <FileTextOutlined className="text-lg" />
                            )
                        }
                    />
                    <div className="min-w-0">
                        <p className="fontSemi text-sm text-[#2F2F46]">{t("rentalAgreement")}</p>
                        <p className="fontRegular mt-0.5 text-sm text-[#7f8c8d]">{subtext}</p>
                    </div>
                </div>

                {agreementUrl ? (
                    <Button
                        type="link"
                        className="!h-auto shrink-0 !px-0 !font-semibold !text-[#5D5DF6]"
                        onClick={() => setIsModalOpen(true)}>
                        {t("view")} <ExportOutlined className="ml-1 text-xs" />
                    </Button>
                ) : (
                    <Button
                        type="link"
                        className="!h-auto shrink-0 !px-0 !font-semibold !text-[#5D5DF6]"
                        onClick={() => setIsGenerateModalOpen(true)}>
                        {t("generateAgreement")}
                    </Button>
                )}
            </div>

            <CheckInAgreementModal
                open={isGenerateModalOpen}
                reservation={resDetails}
                onCancel={() => setIsGenerateModalOpen(false)}
                onAgreeAndContinue={handleAgreementGenerated}
                primaryActionLabel={t("generateAgreement")}
                signatureLabel={t("generateAgreementRenterSignatureLabel")}
            />

            {agreementUrl ? (
                <AgreementModal
                    agreementUrl={agreementUrl}
                    resDetails={resDetails}
                    isModalOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    t={t}
                />
            ) : null}
        </>
    );
};

RentalAgreementRow.propTypes = {
    resDetails: PropTypes.object,
    t: PropTypes.func.isRequired,
    onRefresh: PropTypes.func,
    signedDate: PropTypes.string,
};

export default RentalAgreementRow;
